import { Dispatch, useMemo } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { QuoteAmounts } from "@buildpy/core/modules/quote/action-types";
import { CalcQuoteResult, RATE_SCALE } from "@buildpy/core/modules/quote/calc";
import { DEFAULT_VAT_MODE, storedSplitOf, VatMode } from "@buildpy/core/modules/money/vat";
import Money from "@/components/money";
import { formatWon } from "@buildpy/core/lib/format";
import { formatRate } from "@/modules/quote/ui/format";
import NumberField from "@/modules/quote/ui/editor/number-field";
import { EditorAction } from "@buildpy/core/modules/quote/editor/types";
import { lockedNow, NEVER_LOCKED, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";

interface Props {
  amounts: QuoteAmounts;
  /** 지금 입력 기준의 자동 절사액. 「자동」을 누르면 이 값이 쓰인다. */
  autoRounding: number;
  rounding: number | null;
  /** true 면 아직 저장 전 값이다. */
  live: boolean;
  dispatch: Dispatch<EditorAction>;
  vatMode?: VatMode;
  // 원가·마진 보기가 켜졌을 때만 원가 묶음을 그린다.
  showCost?: boolean;
  cost?: CostSummary | null;
  // 잠긴 견적 — 단수 정리 칸·「자동」 을 끄고, 재렌더 전에 들어온 입력도 isLocked 로 버린다.
  disabled?: boolean;
  isLocked?: () => boolean;
}

export type CostSummary = Pick<CalcQuoteResult, "cost_total" | "cost_missing_count" | "profit" | "profit_rate_bp">;

// 공급가·부가세 — core `storedSplitOf` 한 벌.
export const vatSplitOf = (amounts: Pick<QuoteAmounts, "total" | "supply_amount" | "vat_amount">): { supply: number; vat: number } => {
  const split = storedSplitOf(amounts);
  return { supply: split.supply_amount, vat: split.vat_amount };
};

const Line = ({ label, value, small = false }: { label: string; value: number; small?: boolean }) => (
  <div className={small ? "flex items-baseline justify-between gap-x-4 py-0.5 text-xs" : "flex items-baseline justify-between gap-x-4 py-1"}>
    <span className={small ? "text-muted-foreground" : "text-sm text-muted-foreground"}>{label}</span>
    <Money value={value} />
  </div>
);

// 원가 합계·예상 이익·이익률 — 원가가 빠진 줄이 있으면 이익 대신 그 줄 수를 말한다.
export const CostBlock = ({ cost }: { cost: CostSummary }) => (
  <div className="mt-2 border-t border-border pt-2" data-testid="totals-cost">
    <Line label="원가 합계 (부가세 별도)" value={cost.cost_total} />
    {cost.cost_missing_count > 0 || cost.profit === null ? (
      <p className="py-1 text-right text-xs text-muted-foreground">
        원가 미입력 {cost.cost_missing_count}줄 — 이익은 다 채우면 보입니다
      </p>
    ) : (
      <>
        <Line label="예상 이익" value={cost.profit} />
        <div className="flex items-baseline justify-between gap-x-4 py-1">
          <span className="text-sm text-muted-foreground">이익률</span>
          <span className="tabular-nums">
            {cost.profit_rate_bp === null ? "—" : formatRate(cost.profit_rate_bp / RATE_SCALE)}
          </span>
        </div>
      </>
    )}
  </div>
);

/** 합계. 입력을 고치는 즉시 여기가 바뀌고, 저장하면 서버가 계산한 값으로 바뀐다. */
const TotalsPanel = ({
  amounts,
  autoRounding,
  rounding,
  live,
  dispatch: rawDispatch,
  vatMode = DEFAULT_VAT_MODE,
  showCost = false,
  cost = null,
  disabled = false,
  isLocked = NEVER_LOCKED,
}: Props) => {
  const dispatch = useMemo(() => unlessLocked(lockedNow(disabled, isLocked), rawDispatch), [disabled, isLocked, rawDispatch]);
  const { supply, vat } = vatSplitOf(amounts);
  const excl = vatMode === "EXCL";
  return (
    <SectionCard as="div" padding="sm">
      <div className="flex items-center justify-between pb-1">
        <span className="text-sm font-medium">합계</span>
        <span className="text-xs text-muted-foreground">
          {live ? "저장 전 계산값" : "저장된 값"}
        </span>
      </div>

      <Line label="기본 공사 소계" value={amounts.subtotal_basic} />
      <Line label="스타일 공사 소계" value={amounts.subtotal_style} />
      <Line label="관리비" value={amounts.mgmt_fee} />
      <Line label={excl ? "총계 (부가세 별도)" : "총계"} value={amounts.gross} />

      <div className="flex items-center justify-between gap-x-2 border-t border-border py-1">
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          단수 정리
        </span>
        <div className="flex items-center gap-x-1">
          <NumberField
            ariaLabel="단수 정리"
            className="w-24"
            disabled={disabled}
            value={rounding ?? autoRounding}
            variant="outline"
            onCommit={(value) => dispatch({ type: "SET_ROUNDING", value })}
          />
          <Button
            className="whitespace-nowrap"
            disabled={rounding === null || disabled}
            size="sm"
            variant="outline"
            onClick={() => dispatch({ type: "SET_ROUNDING", value: null })}>
            {/* 「자동」 이 지금 얼마를 깎는지 눌러 보기 전에 보여 준다. */}
            자동 (−{formatWon(autoRounding)})
          </Button>
        </div>
      </div>
      {rounding === null && (
        <p className="pb-1 text-right text-xs text-muted-foreground">
          자동 — 총계의 만원 미만을 깎습니다
        </p>
      )}

      {excl && (
        <div className="border-t border-border pt-1" data-testid="totals-vat">
          <Line label="공급가액" value={supply} />
          <Line label="부가세 (10%)" value={vat} />
        </div>
      )}

      <div className="flex items-baseline justify-between gap-x-4 border-t border-border pt-2">
        <span className="text-sm font-medium">최종 견적금액</span>
        <span className="text-xl font-bold tabular-nums">
          <Money value={amounts.total} />
        </span>
      </div>
      <p className="pt-0.5 text-right text-xs text-muted-foreground">
        부가세 포함
      </p>
      {!excl && (
        <div className="pt-1" data-testid="totals-vat">
          <Line label="공급가액" small value={supply} />
          <Line label="부가세 (10%)" small value={vat} />
        </div>
      )}

      {showCost && cost && <CostBlock cost={cost} />}
    </SectionCard>
  );
};

export default TotalsPanel;
